import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { getStepConnectionByUuid, listStepConnections } from "./whatsappFlowApi";
import { flowRoleLabel } from "./whatsappFlowCatalogs";

const ButtonPrincipal = styled(Button)({
  borderRadius: "20px",
  backgroundColor: "#161e55",
  color: "#ffff",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#A8C8AF",
  },
});

const ButtonSecondary = styled(Button)({
  borderRadius: "20px",
  color: "#161e55",
  borderColor: "#161e55",
  textTransform: "none",
});

const flowOptionLabel = (option) => {
  const name = option?.name != null ? String(option.name).trim() : "";
  return name !== "" ? name : option?.uuid ?? "";
};

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

/**
 * Diálogo para elegir un flujo existente y pegar sus pasos en el flujo actual.
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   excludeUuid?: string,
 *   requiresConfirmation: boolean,
 *   onConfirm: (flow: { uuid: string, name: string, nodes: object[], edges: object[] }) => void,
 * }} props
 */
export default function WhatsappFlowCopyDialog({
  open,
  onClose,
  excludeUuid,
  requiresConfirmation,
  onConfirm,
}) {
  const [flows, setFlows] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loadingFlow, setLoadingFlow] = useState(false);
  const [flowError, setFlowError] = useState(null);
  const [pendingFlow, setPendingFlow] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setSelected(null);
    setFlowError(null);
    setPendingFlow(null);
    setListError(null);
    setListLoading(true);

    listStepConnections()
      .then((items) => {
        if (cancelled) return;
        setFlows(items.filter((item) => item?.uuid && item.uuid !== excludeUuid));
      })
      .catch((error) => {
        if (cancelled) return;
        setFlows([]);
        setListError(errorMessage(error, "No se pudo cargar la lista de flujos."));
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, excludeUuid]);

  const handleCopy = async () => {
    if (!selected) return;
    setFlowError(null);
    setLoadingFlow(true);
    try {
      const flow = await getStepConnectionByUuid(selected.uuid);
      const nodes = Array.isArray(flow.nodes) ? flow.nodes : [];
      if (nodes.length === 0) {
        setFlowError("El flujo seleccionado no tiene pasos.");
        return;
      }
      const loaded = {
        uuid: selected.uuid,
        name: flowOptionLabel(selected),
        nodes,
        edges: Array.isArray(flow.edges) ? flow.edges : [],
      };
      if (requiresConfirmation) {
        setPendingFlow(loaded);
      } else {
        onConfirm(loaded);
      }
    } catch (error) {
      setFlowError(errorMessage(error, "No se pudo cargar el flujo seleccionado."));
    } finally {
      setLoadingFlow(false);
    }
  };

  const handleConfirmPaste = () => {
    if (!pendingFlow) return;
    onConfirm(pendingFlow);
  };

  return (
    <Dialog open={open} onClose={loadingFlow ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copiar pasos de otro flujo</DialogTitle>
      <DialogContent>
        {pendingFlow ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Se agregarán {pendingFlow.nodes.length}{" "}
            {pendingFlow.nodes.length === 1 ? "paso" : "pasos"} de «{pendingFlow.name}» al flujo
            actual, sin conectarlos a los pasos existentes. No se guardará nada hasta que pulses
            Guardar.
          </Alert>
        ) : (
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Los pasos del flujo elegido, con sus conexiones, se agregarán al flujo actual. El
              nombre, la descripción y el tipo del flujo actual no cambian.
            </Typography>
            {listError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {listError}
              </Alert>
            ) : null}
            <Autocomplete
              options={flows}
              loading={listLoading}
              value={selected}
              onChange={(_event, value) => {
                setSelected(value);
                setFlowError(null);
              }}
              getOptionLabel={flowOptionLabel}
              isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
              noOptionsText={listLoading ? "Cargando flujos..." : "No hay otros flujos."}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.uuid}>
                  <Box>
                    <Typography variant="body2">{flowOptionLabel(option)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {flowRoleLabel(option.flowRole || "PRIMARY")}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Flujo a copiar"
                  placeholder="Busca por nombre"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {listLoading ? <CircularProgress size={18} sx={{ color: "#161e55" }} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {flowError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {flowError}
              </Alert>
            ) : null}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {pendingFlow ? (
          <>
            <ButtonSecondary variant="outlined" onClick={() => setPendingFlow(null)}>
              Cancelar
            </ButtonSecondary>
            <ButtonPrincipal variant="contained" onClick={handleConfirmPaste}>
              Agregar pasos
            </ButtonPrincipal>
          </>
        ) : (
          <>
            <ButtonSecondary variant="outlined" onClick={onClose} disabled={loadingFlow}>
              Cancelar
            </ButtonSecondary>
            <ButtonPrincipal
              variant="contained"
              onClick={handleCopy}
              disabled={!selected || loadingFlow}
              startIcon={loadingFlow ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Copiar pasos
            </ButtonPrincipal>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
